import "./modal.css";

import { SynthButton } from "./button.ts";

// Modal primitive + three promise-based helpers that replace the
// browser-native `window.alert` / `window.confirm` / `window.prompt`
// inside the synth shell. Built so every dialog reads as part of the
// same neumorphic surface as the rest of the UI.
//
// Public API:
//   showAlert({ title, message, dismissLabel? }):  Promise<void>
//   showConfirm({ ... }):                          Promise<boolean>
//   showPrompt({ ... }):                           Promise<string | null>
//
// All three share a backdrop, focus trap, ESC-to-cancel, and Enter-to-
// confirm. The backdrop click also cancels (Apple HIG modal-escape).

export interface AlertOptions {
  title: string;
  message?: string;
  dismissLabel?: string;
}

export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Destructive actions get amber-tinted primary button styling and
   *  the cancel button as the default keyboard focus, so accidental
   *  Enter doesn't fire the dangerous path. */
  destructive?: boolean;
}

export interface PromptOptions {
  title: string;
  message?: string;
  defaultValue?: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

type DialogKind = "alert" | "confirm" | "prompt";

interface BuildArgs {
  kind: DialogKind;
  title: string;
  message?: string;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  defaultValue?: string;
  placeholder?: string;
  resolve: (value: string | boolean | null) => void;
}

let activeModal: HTMLElement | null = null;
let previousFocus: Element | null = null;

function close(root: HTMLElement, returnValue: string | boolean | null, resolve: (value: any) => void): void {
  // Guard against double-close from chained handlers (e.g. Enter +
  // backdrop click during the same tick).
  if (root.dataset["closing"] === "true") return;
  root.dataset["closing"] = "true";
  root.classList.add("is-closing");
  // 120ms matches the CSS exit transition; if reduced-motion is on
  // the CSS clamps the transition to 0 so the timer fires immediately.
  const exitMs = matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 120;
  window.setTimeout(() => {
    root.remove();
    if (activeModal === root) activeModal = null;
    if (previousFocus instanceof HTMLElement) previousFocus.focus();
    previousFocus = null;
    resolve(returnValue);
  }, exitMs);
}

function trapFocus(panel: HTMLElement, ev: KeyboardEvent): void {
  if (ev.key !== "Tab") return;
  const focusables = Array.from(
    panel.querySelectorAll<HTMLElement>(
      'button:not(:disabled), input:not(:disabled), [tabindex]:not([tabindex="-1"])',
    ),
  );
  if (focusables.length === 0) return;
  const first = focusables[0]!;
  const last = focusables[focusables.length - 1]!;
  const active = document.activeElement;
  if (ev.shiftKey && active === first) {
    ev.preventDefault();
    last.focus();
  } else if (!ev.shiftKey && active === last) {
    ev.preventDefault();
    first.focus();
  }
}

function buildModal(args: BuildArgs): void {
  previousFocus = document.activeElement;

  const root = document.createElement("div");
  root.className = "modal-root";
  root.setAttribute("role", "presentation");

  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  root.append(backdrop);

  const panel = document.createElement("div");
  panel.className = "modal-panel";
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-modal", "true");

  const titleId = `modal-title-${Math.random().toString(36).slice(2, 8)}`;
  panel.setAttribute("aria-labelledby", titleId);

  const title = document.createElement("h2");
  title.className = "modal-title";
  title.id = titleId;
  title.textContent = args.title;
  panel.append(title);

  if (args.message) {
    const msg = document.createElement("p");
    msg.className = "modal-message";
    msg.textContent = args.message;
    panel.append(msg);
  }

  // Prompt input lives between the message and the action row.
  let input: HTMLInputElement | null = null;
  if (args.kind === "prompt") {
    const field = document.createElement("div");
    field.className = "modal-field";
    input = document.createElement("input");
    input.type = "text";
    input.className = "modal-input";
    input.value = args.defaultValue ?? "";
    if (args.placeholder) input.placeholder = args.placeholder;
    input.setAttribute("aria-label", args.title);
    field.append(input);
    panel.append(field);
  }

  // Actions — cancel left, primary right. Destructive variant tints
  // the primary button amber and defaults focus to cancel.
  const actions = document.createElement("div");
  actions.className = "modal-actions";
  if (args.destructive) actions.classList.add("modal-actions-destructive");
  panel.append(actions);

  let cancelBtn: SynthButton | null = null;
  if (args.kind !== "alert") {
    const cancelSlot = document.createElement("div");
    actions.append(cancelSlot);
    cancelBtn = new SynthButton({
      container: cancelSlot,
      label: args.cancelLabel ?? "cancel",
      variant: "momentary",
      onPress: () => {
        const fallback = args.kind === "confirm" ? false : null;
        close(root, fallback, args.resolve);
      },
    });
  }

  const confirmSlot = document.createElement("div");
  confirmSlot.className = "modal-primary-slot";
  actions.append(confirmSlot);
  const confirmBtn = new SynthButton({
    container: confirmSlot,
    label: args.confirmLabel,
    variant: "momentary",
    ...(args.destructive ? { intent: "red" as const } : {}),
    onPress: () => {
      if (args.kind === "alert") {
        close(root, null, args.resolve);
      } else if (args.kind === "confirm") {
        close(root, true, args.resolve);
      } else if (input) {
        close(root, input.value, args.resolve);
      }
    },
  });

  root.append(panel);
  document.body.append(root);

  // Trigger the enter animation on the next frame so the initial
  // off-screen state is committed first.
  requestAnimationFrame(() => root.classList.add("is-open"));

  activeModal = root;

  // Keyboard handling: ESC = cancel, Enter = confirm, Tab traps focus.
  const onKey = (ev: KeyboardEvent): void => {
    if (ev.key === "Escape") {
      ev.preventDefault();
      const fallback = args.kind === "confirm" ? false : args.kind === "prompt" ? null : undefined;
      close(root, fallback as string | boolean | null, args.resolve);
    } else if (ev.key === "Enter") {
      // For prompt the input owns Enter naturally; only intercept when
      // focus is somewhere else (so the user can press Enter from any
      // focused button to confirm too).
      if (args.kind === "prompt" && document.activeElement === input) {
        ev.preventDefault();
        close(root, input!.value, args.resolve);
      } else if (args.kind !== "prompt") {
        ev.preventDefault();
        if (args.kind === "alert") {
          close(root, null, args.resolve);
        } else {
          close(root, true, args.resolve);
        }
      }
    } else if (ev.key === "Tab") {
      trapFocus(panel, ev);
    }
  };
  panel.addEventListener("keydown", onKey);

  backdrop.addEventListener("click", () => {
    const fallback = args.kind === "confirm" ? false : args.kind === "prompt" ? null : null;
    close(root, fallback, args.resolve);
  });

  // Initial focus — prompt focuses + selects the input so the user can
  // type immediately. Destructive confirms focus cancel as a safety
  // gate. Everything else focuses the primary button.
  requestAnimationFrame(() => {
    if (input) {
      input.focus();
      input.select();
    } else if (args.destructive && cancelBtn) {
      (cancelBtn as unknown as { root: HTMLButtonElement }).root.focus();
    } else {
      (confirmBtn as unknown as { root: HTMLButtonElement }).root.focus();
    }
  });
}

export function showAlert(opts: AlertOptions): Promise<void> {
  return new Promise<void>((resolve) => {
    buildModal({
      kind: "alert",
      title: opts.title,
      ...(opts.message ? { message: opts.message } : {}),
      confirmLabel: opts.dismissLabel ?? "ok",
      resolve: () => resolve(),
    });
  });
}

export function showConfirm(opts: ConfirmOptions): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    buildModal({
      kind: "confirm",
      title: opts.title,
      ...(opts.message ? { message: opts.message } : {}),
      confirmLabel: opts.confirmLabel ?? "confirm",
      cancelLabel: opts.cancelLabel ?? "cancel",
      ...(opts.destructive ? { destructive: true } : {}),
      resolve: (value) => resolve(value === true),
    });
  });
}

export function showPrompt(opts: PromptOptions): Promise<string | null> {
  return new Promise<string | null>((resolve) => {
    buildModal({
      kind: "prompt",
      title: opts.title,
      ...(opts.message ? { message: opts.message } : {}),
      confirmLabel: opts.confirmLabel ?? "save",
      cancelLabel: opts.cancelLabel ?? "cancel",
      ...(opts.defaultValue !== undefined ? { defaultValue: opts.defaultValue } : {}),
      ...(opts.placeholder ? { placeholder: opts.placeholder } : {}),
      resolve: (value) => resolve(typeof value === "string" ? value : null),
    });
  });
}
